import { useCallback, useEffect, useState } from 'react';

/**
 * A custom hook that runs an interval function only when the browser tab is visible.
 * The interval is automatically cleared when the tab becomes invisible and
 * recreated when the tab becomes visible again.
 *
 * @param callback - The function to be called at each interval
 * @param delay - The interval delay in milliseconds
 * @returns void
 *
 * @example
 * ```tsx
 * // Example usage
 * useVisibilityAwareInterval(() => {
 *   customTableEventDispatcher(CustomTableEventModel.REFRESH);
 * }, 15000);
 * ```
 */
export const useVisibilityAwareInterval = (callback: () => void, delay: number): void => {
  const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');

  const handleVisibilityChange = useCallback(() => {
    setIsVisible(document.visibilityState === 'visible');
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let interval: NodeJS.Timeout | null = null;

    if (isVisible) {
      interval = setInterval(callback, delay);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isVisible, handleVisibilityChange, callback, delay]);
};
